import { Injectable, inject } from '@angular/core';
import { Encounter, Observation, Resource, ServiceRequest, Specimen, MedicationDispense, MedicationAdministration, Task, OperationOutcome, BundleEntry, ChargeItemDefinition, ChargeItem, Invoice, DiagnosticReport, SupplyDelivery, ActivityDefinition, ImagingStudy, Procedure } from 'fhir/r4';
import { Condition, Medication, MedicationRequest, Patient, Location, CodeSystem, ValueSet, Schedule, Slot, AllergyIntolerance, Appointment, Immunization, HealthcareService } from 'fhir/r4';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, Observable, of, shareReplay } from 'rxjs';
import { Bundle, Reference } from 'fhir/r4';
import { ErrorService } from './error.service';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { ReferenceDataType } from './dynamic-forms.interface2';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);

  // Inject ErrorService for error handling
  private errorService = inject(ErrorService);

  // constructor() { }

  currentEncounter: BehaviorSubject<null | Encounter & {
    status: Encounter['status'],
    patientId: string,
    [key: string]: any
  }> = new BehaviorSubject<null | Encounter & {
    status: Encounter['status'],
    patientId: string,
    [key: string]: any
  }>(null);

  currentPatientIdFromResolver = new BehaviorSubject<string | null>(null);

  // ✅ BehaviorSubject version of current patient ID with multiple source fallback
  currentPatientId$ = new BehaviorSubject<string | null>(null);

  constructor() {
    // Subscribe to combined sources and update the currentPatientId$ BehaviorSubject
    combineLatest([
      this.PatientResources.currentPatient,
      this.currentEncounter,
      this.currentPatientIdFromResolver
    ]).pipe(
      map(([patientWrapper, encounter, resolverId]) => {
        // Priority 1: Check encounter's patient reference
        if (encounter?.['subject']?.reference) {
          const patientRef = encounter['subject'].reference;
          const match = patientRef.match(/^Patient\/(.+)$/);
          if (match) return match[1];
          if (!patientRef.includes('/')) return patientRef;
        }

        // Priority 2: Check current patient resource
        if (patientWrapper?.actualResource?.id) {
          return patientWrapper.actualResource.id;
        }

        // Priority 3: Check patient ID from resolver
        if (resolverId) {
          return resolverId;
        }

        // No patient ID found
        return null;
      }),
      distinctUntilChanged() // Only emit when ID actually changes
    ).subscribe(patientId => {
      this.currentPatientId$.next(patientId);
    });

    // Reactive sync for currentEncounterResources from PatientResources
    this.syncEncounterResources('observations', this.PatientResources.observations, this.currentEncounterResources.observations);
    this.syncEncounterResources('condition', this.PatientResources.condition, this.currentEncounterResources.diagnosis);
    this.syncEncounterResources('medicationRequests', this.PatientResources.medicationRequests, this.currentEncounterResources.medicationRequests);
  }

  /**
   * Helper to sync encounter-specific resources from general patient resources
   */
  private syncEncounterResources(
    _type: string,
    source$: BehaviorSubject<any[]>,
    target$: BehaviorSubject<any[]>
  ) {
    combineLatest([
      this.currentEncounter.pipe(
        map(enc => enc?.id),
        distinctUntilChanged()
      ),
      source$
    ]).subscribe(([encounterId, resources]) => {
      if (!encounterId) {
        if (target$.value.length > 0) target$.next([]);
        return;
      }
      const filtered = resources.filter(r => this.isResourceForCurrentEncounter(r.actualResource));
      
      // Only emit if the list actually changed (shallow length + id check)
      const currentIds = target$.value.map(r => r.referenceId).join(',');
      const nextIds = filtered.map(r => r.referenceId).join(',');
      
      if (currentIds !== nextIds) {
        target$.next(filtered);
      }
    });
  }
  // ]).pipe(
  //   map(([patientWrapper, encounter, resolverId]) => {
  //     // Priority 1: Check encounter's patient reference
  //     if (encounter?.['subject']?.reference) {
  //       const patientRef = encounter['subject'].reference;
  //       const match = patientRef.match(/^Patient\/(.+)$/);
  //       if (match) return match[1];
  //       if (!patientRef.includes('/')) return patientRef;
  //     }

  //     // Priority 2: Check current patient resource
  //     if (patientWrapper?.actualResource?.id) {
  //       return patientWrapper.actualResource.id;
  //     }

  //     // Priority 3: Check patient ID from resolver
  //     if (resolverId) {
  //       return resolverId;
  //     }

  //     // No patient ID found
  //     return null;
  //   }),
  //   distinctUntilChanged(), // Only emit when ID actually changes
  //   shareReplay(1) // Cache latest value for late subscribers
  // );

  //have all current encounter Resources here as beaahiour subject with referenceId, savedStatus & actualResources
  currentEncounterResources = {
    observations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Observation
    }>>([]),
    diagnosis: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Condition
    }>>([]),
    medicationRequests: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationRequest
    }>>([]),



  }

  //full resources for patient

  PatientResources = {
    //allergyIn
    // allergyIntolerances: new BehaviorSubject<Array<{
    //   referenceId: string | null,
    //   savedStatus: 'saved' | 'unsaved',
    //   actualResource: AllergyIntolerance
    // }>>([]),
    observations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Observation
    }>>([]),
    //add more resource types as needed
    condition: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Condition
    }>>([]),
    medicationRequests: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationRequest
    }>>([]),
    allergyIntolerances: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: AllergyIntolerance
    }>>([]),
    specimens: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Specimen
    }>>([]),
    medications: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Medication
    }>>([]),
    medicationDispenses: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationDispense
    }>>([]),
    medicationAdministrations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationAdministration
    }>>([]),
    encounters: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Encounter
    }>>([]),
    currentPatient: new BehaviorSubject<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Patient
    }>({
      referenceId: null,
      savedStatus: 'unsaved',
      actualResource: {} as Patient
    }),
    serviceRequests: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Resource
    }>>([]),
    tasks: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Task
    }>>([]),
    chargeItems: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ChargeItem
    }>>([]),
    invoices: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Invoice
    }>>([]),
    diagnosticReports: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: DiagnosticReport
    }>>([]),
    procedures: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Procedure
    }>>([]),
    imagingStudies: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ImagingStudy
    }>>([]),
    appointments: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Appointment
    }>>([]),
    immunizations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Immunization
    }>>([]),
  }



  orgWideResources = {
    patient: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Patient
    }>>([]),

    observations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Observation
    }>>([]),
    condition: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Condition
    }>>([]),
    medicationRequests: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationRequest
    }>>([]),
    specimens: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Specimen
    }>>([]),
    medications: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Medication
    }>>([]),
    medicationDispenses: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationDispense
    }>>([]),
    medicationAdministrations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: MedicationAdministration
    }>>([]),
    encounters: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Encounter
    }>>([]),
    currentPatient: new BehaviorSubject<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Patient
    }>({
      referenceId: null,
      savedStatus: 'unsaved',
      actualResource: {} as Patient
    }),
    serviceRequests: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Resource
    }>>([]),

    healthcareServices: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: HealthcareService
    }>>([]),

    procedures: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Procedure
    }>>([]),
    imagingStudies: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ImagingStudy
    }>>([]),
    tasks: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Task
    }>>([]),
    locations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Location
    }>>([]),
    codeSystems: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: CodeSystem
    }>>([]),
    valueSets: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ValueSet
    }>>([]),
    chargeItemDefinitions: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ChargeItemDefinition
    }>>([]),
    chargeItems: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ChargeItem
    }>>([]),
    invoices: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Invoice
    }>>([]),
    diagnosticReports: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: DiagnosticReport
    }>>([]),

    activityDefinitions: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: ActivityDefinition
    }>>([]),

    supplyDeliveries: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: SupplyDelivery
    }>>([]),
    schedules: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Schedule
    }>>([]),
    slots: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Slot
    }>>([]),
    appointments: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Appointment
    }>>([]),
    immunizations: new BehaviorSubject<Array<{
      referenceId: string | null,
      savedStatus: 'saved' | 'unsaved',
      actualResource: Immunization
    }>>([]),

  }

  private encounterChecklistCompleted = new BehaviorSubject<boolean>(false);
  encounterChecklistCompleted$ = this.encounterChecklistCompleted.asObservable();

  setCurrentEncounter(encounter: null | Encounter & {
    status?: Encounter['status'],
    patientId?: string,
    [key: string]: any
  } | any) {
    let normalized = encounter;
    if (encounter && !encounter.patientId && encounter.subject?.reference) {
      const extractedId = encounter.subject.reference.split('/').pop();
      if (extractedId) {
        normalized = { ...encounter, patientId: extractedId };
      }
    }
    (this.currentEncounter as BehaviorSubject<null | Encounter & {
      status: Encounter['status'],
      patientId: string,
      [key: string]: any
    }>).next(normalized);
  }

  currentHospital: Observable<{
    id: string,
    name: string,
    [key: string]: any
  }> = new BehaviorSubject({
    id: '1234567890',
    name: 'e-Likita General Hospital'
  })

  setCurrentHospital(hospital: {
    id: string,
    name: string,
    [key: string]: any
  }) {
    (this.currentHospital as BehaviorSubject<{
      id: string,
      name: string,
      [key: string]: any
    }>).next(hospital);
  }

  setEncounterChecklistCompleted(completed: boolean) {
    this.encounterChecklistCompleted.next(completed);
  }

  resetEncounterChecklist() {
    this.encounterChecklistCompleted.next(false);
  }

  addEncounterToPatientResources(encounter: Encounter, savedStatus: 'saved' | 'unsaved' = 'unsaved') {
    const item = {
      referenceId: this.toRefId(encounter),
      savedStatus,
      actualResource: encounter
    };
    this.upsertToSubject(this.PatientResources.encounters, item);
  }

  /**
   * Accepts a FHIR Bundle (e.g., type: 'transaction' or 'batch'), iterates entries,
   * and adds resources to PatientResources. If a resource is linked to the current
   * encounter (resource.encounter.reference === 'Encounter/{currentEncounterId}'),
   * it will also be added to currentEncounterResources in the appropriate collection.
   */
  processBundleTransaction(bundle: Bundle) {
    const entries = bundle?.entry ?? [];
    for (const e of entries) {
      const resource = e.resource as Resource | undefined;
      if (!resource) continue;
      const status: 'saved' | 'unsaved' = resource.id ? 'saved' : 'unsaved';

      // Add to overall patient resources
      this.addResourceToPatientResources(resource, status);
// alert('added');
      // If tied to the current encounter, mirror into currentEncounterResources
      if (this.isResourceForCurrentEncounter(resource)) {
        this.addResourceToCurrentEncounterResources(resource, status);
      }
    }
  }

  processOrgWideBundleTransaction(bundle: Bundle) {
    const entries = bundle?.entry ?? [];
    for (const entry of entries) {
      const resource = entry.resource as Resource | undefined;
      if (!resource) continue;
      const status: 'saved' | 'unsaved' = resource.id ? 'saved' : 'unsaved';
      this.addResourceToOrgWideResources(resource, status);
    }
  }

  // ---------------------- Helpers ----------------------

  public toRefId(resource: Resource | { resourceType?: string; id?: string } | undefined | null): string | null {
    if (!resource || !resource.resourceType || !resource.id) return null;
    return `${resource.resourceType}/${resource.id}`;
  }

  private getCurrentEncounterId(): string | null {
    const ce = this.currentEncounter.value;
    const id = (ce && (ce as any).id) ? String((ce as any).id) : null;
    return id ?? null;
  }

  getCurrentEncounterReference(): Reference | null {
    const id = this.getCurrentEncounterId();
    return id ? this.buildReference('Encounter', id) : null;
  }

  getPatientReference(): Reference | null {
    const patient = this.PatientResources.currentPatient.value?.actualResource;
    const id = patient?.id;
    return id ? this.buildReference('Patient', id) : null;
  }

  getPractitionerReference(practitionerId?: string | null): Reference | null {
    // Provide the ID from the caller (e.g., from auth.user.userId). We avoid injecting AuthService here.
    if (!practitionerId) return null;
    return this.buildReference('Practitioner', practitionerId);
  }

  private buildReference(resourceType: 'Patient' | 'Encounter' | 'Practitioner', id: string): Reference {
    return { reference: `${resourceType}/${id}` };
  }

  public isResourceForCurrentEncounter(resource: Resource): boolean {
    const currentEncId = this.getCurrentEncounterId();
    if (!currentEncId || !resource) return false;

    // Check encounter reference in the resource
    const encRef = (resource as any)?.encounter?.reference as string | undefined;
    if (!encRef) return false;

    // Extract ID from reference (handles 'Encounter/123', '123', or full URLs)
    const refId = encRef.includes('/') ? encRef.split('/').pop() : encRef;
    
    return refId === currentEncId;
  }

  private upsertToSubject<T extends { referenceId: string | null }>(
    subject: BehaviorSubject<T[]>,
    item: T
  ) {
    const list = subject.value.slice();
    const idx = item.referenceId

      ? list.findIndex(x => x.referenceId === item.referenceId)
      : -1;
    if (idx > -1) list[idx] = item;
    else list.unshift(item);
    subject.next(list);
  }

  private addResourceToPatientResources(resource: Resource, savedStatus: 'saved' | 'unsaved' = 'unsaved') {
    const referenceId = this.toRefId(resource);
    switch (resource.resourceType) {
      case 'Observation': {
        this.upsertToSubject(this.PatientResources.observations, {
          referenceId,
          savedStatus,
          actualResource: resource as Observation
        });
        break;
      }
      case 'Condition': {
        this.upsertToSubject(this.PatientResources.condition, {
          referenceId,
          savedStatus,
          actualResource: resource as Condition
        });
        break;
      }
      case 'MedicationRequest': {
        this.upsertToSubject(this.PatientResources.medicationRequests, {
          referenceId,
          savedStatus,
          actualResource: resource as MedicationRequest
        });
        break;
      }
      case 'Specimen': {
        this.upsertToSubject(this.PatientResources.specimens, {
          referenceId,
          savedStatus,
          actualResource: resource as Specimen
        });
        break;
      }
      case 'Medication': {
        this.upsertToSubject(this.PatientResources.medications, {
          referenceId,
          savedStatus,
          actualResource: resource as Medication
        });
        break;
      }
      case 'MedicationDispense': {
        this.upsertToSubject(this.PatientResources.medicationDispenses, {
          referenceId,
          savedStatus,
          actualResource: resource as MedicationDispense
        });
        break;
      }
      case 'MedicationAdministration': {
        this.upsertToSubject(this.PatientResources.medicationAdministrations, {
          referenceId,
          savedStatus,
          actualResource: resource as MedicationAdministration
        });
        break;
      }
      case 'Encounter': {
        this.addEncounterToPatientResources(resource as Encounter, savedStatus);
        break;
      }
      case 'Patient': {
        this.PatientResources.currentPatient.next({
          referenceId,
          savedStatus,
          actualResource: resource as Patient
        });
        break;
      }
      // case 'ServiceRequest': {
      //   this.upsertToSubject(this.PatientResources.serviceRequests, {
      //     referenceId,
      //     savedStatus,
      //     actualResource: resource as ServiceRequest
      //   });
      //   break;

      // }
      case 'ServiceRequest': {
        this.upsertToSubject(this.PatientResources.serviceRequests, {
          referenceId,
          savedStatus,
          actualResource: resource as ServiceRequest
        });
        break;
      }
      case 'Task': {
        this.upsertToSubject(this.PatientResources.tasks, {
          referenceId,
          savedStatus,
          actualResource: resource as Task
        });
        break;
      }
      case 'DiagnosticReport': {
        this.upsertToSubject(this.PatientResources.diagnosticReports, {
          referenceId,
          savedStatus,
          actualResource: resource as DiagnosticReport
        });
        break;
      }
      case 'ChargeItem': {
        this.upsertToSubject(this.PatientResources.chargeItems, {
          referenceId,
          savedStatus,
          actualResource: resource as ChargeItem
        });
        break;
      }
      case 'Invoice': {
        this.upsertToSubject(this.PatientResources.invoices, {
          referenceId,
          savedStatus,
          actualResource: resource as Invoice
        });
        break;
      }
      case 'Procedure': {
        this.upsertToSubject(this.PatientResources.procedures, {
          referenceId,
          savedStatus,
          actualResource: resource as Procedure
        });
        break;
      }
      case 'ImagingStudy': {
        this.upsertToSubject(this.PatientResources.imagingStudies, {
          referenceId,
          savedStatus,
          actualResource: resource as ImagingStudy
        });
        break;
      }
      case 'AllergyIntolerance': {
        this.upsertToSubject(this.PatientResources.allergyIntolerances, {
          referenceId,
          savedStatus,
          actualResource: resource as AllergyIntolerance
        });
        break;
      }
      case 'Appointment': {
        this.upsertToSubject(this.PatientResources.appointments, {
          referenceId,
          savedStatus,
          actualResource: resource as Appointment
        });
        break;
      }
      case 'Immunization': {
        this.upsertToSubject(this.PatientResources.immunizations, {
          referenceId,
          savedStatus,
          actualResource: resource as Immunization
        });
        break;
      }
      default:
        // Unsupported resource types can be handled here if needed.
        break;
    }
  }

  private addResourceToOrgWideResources(resource: Resource, savedStatus: 'saved' | 'unsaved' = 'unsaved') {
    const referenceId = this.toRefId(resource);

    switch (resource.resourceType) {
      case 'Patient':
        this.upsertToSubject(this.orgWideResources.patient, { referenceId, savedStatus, actualResource: resource as Patient });
        break;
      case 'Observation':
        this.upsertToSubject(this.orgWideResources.observations, { referenceId, savedStatus, actualResource: resource as Observation });
        break;
      case 'Condition':
        this.upsertToSubject(this.orgWideResources.condition, { referenceId, savedStatus, actualResource: resource as Condition });
        break;
      case 'MedicationRequest':
        this.upsertToSubject(this.orgWideResources.medicationRequests, { referenceId, savedStatus, actualResource: resource as MedicationRequest });
        break;
      case 'Specimen':
        this.upsertToSubject(this.orgWideResources.specimens, { referenceId, savedStatus, actualResource: resource as Specimen });
        break;
      case 'Medication':
        this.upsertToSubject(this.orgWideResources.medications, { referenceId, savedStatus, actualResource: resource as Medication });
        break;
      case 'MedicationDispense':
        this.upsertToSubject(this.orgWideResources.medicationDispenses, { referenceId, savedStatus, actualResource: resource as MedicationDispense });
        break;
      case 'MedicationAdministration':
        this.upsertToSubject(this.orgWideResources.medicationAdministrations, { referenceId, savedStatus, actualResource: resource as MedicationAdministration });
        break;
      case 'Encounter':
        this.upsertToSubject(this.orgWideResources.encounters, { referenceId, savedStatus, actualResource: resource as Encounter });
        break;
      case 'Procedure':
        this.upsertToSubject(this.orgWideResources.procedures, { referenceId, savedStatus, actualResource: resource as Procedure });
        break;
      case 'ImagingStudy':
        this.upsertToSubject(this.orgWideResources.imagingStudies, { referenceId, savedStatus, actualResource: resource as ImagingStudy });
        break;
      case 'ServiceRequest':
        this.upsertToSubject(this.orgWideResources.serviceRequests, { referenceId, savedStatus, actualResource: resource as ServiceRequest });
        break;
      case 'HealthcareService':
        this.upsertToSubject(this.orgWideResources.healthcareServices, {
          referenceId,
          savedStatus,
          actualResource: resource as HealthcareService
        });
        break;
      case 'Task':
        this.upsertToSubject(this.orgWideResources.tasks, { referenceId, savedStatus, actualResource: resource as Task });
        break;
      case 'Location':
        this.upsertToSubject(this.orgWideResources.locations, { referenceId, savedStatus, actualResource: resource as Location });
        break;
      case 'CodeSystem':
        this.upsertToSubject(this.orgWideResources.codeSystems, { referenceId, savedStatus, actualResource: resource as CodeSystem });
        break;
      case 'ValueSet':
        this.upsertToSubject(this.orgWideResources.valueSets, { referenceId, savedStatus, actualResource: resource as ValueSet });
        break;
      case 'ChargeItemDefinition':
        this.upsertToSubject(this.orgWideResources.chargeItemDefinitions, {
          referenceId,
          savedStatus,
          actualResource: resource as ChargeItemDefinition
        });
        break;
      case 'ChargeItem':
        this.upsertToSubject(this.orgWideResources.chargeItems, {
          referenceId,
          savedStatus,
          actualResource: resource as ChargeItem
        });
        break;
      case 'Invoice':
        this.upsertToSubject(this.orgWideResources.invoices, {
          referenceId,
          savedStatus,
          actualResource: resource as Invoice
        });
        break;
      case 'DiagnosticReport':
        this.upsertToSubject(this.orgWideResources.diagnosticReports, {
          referenceId,
          savedStatus,
          actualResource: resource as DiagnosticReport
        });
        break;
      case 'ActivityDefinition':
        this.upsertToSubject(this.orgWideResources.activityDefinitions, {
          referenceId,
          savedStatus,
          actualResource: resource as ActivityDefinition
        });
        break;
      case 'SupplyDelivery':
        this.upsertToSubject(this.orgWideResources.supplyDeliveries, {
          referenceId,
          savedStatus,
          actualResource: resource as SupplyDelivery
        });
        break;
      case 'Schedule':
        this.upsertToSubject(this.orgWideResources.schedules, {
          referenceId,
          savedStatus,
          actualResource: resource as Schedule
        });
        break;
      case 'Appointment':
        this.upsertToSubject(this.orgWideResources.appointments, {
          referenceId,
          savedStatus,
          actualResource: resource as Appointment
        });
        break;

      case 'Slot':
        this.upsertToSubject(this.orgWideResources.slots, {
          referenceId,
          savedStatus,
          actualResource: resource as Slot
        });
        break;
      case 'Immunization':
        this.upsertToSubject(this.orgWideResources.immunizations, {
          referenceId,
          savedStatus,
          actualResource: resource as Immunization
        });
        break;
      default:
        break;
    }
  }

  // Decide if we should also add a current-encounter resource into PatientResources.
  // This ensures the resource belongs to the same patient as the current PatientResources.currentPatient.
  private maybeAddToPatientResources(resource: Resource, savedStatus: 'saved' | 'unsaved') {
    const encPatientId = this.currentEncounter.value?.patientId ?? null;
    const currentPatientId = this.PatientResources.currentPatient.value?.actualResource?.id ?? null;
    if (encPatientId && currentPatientId && encPatientId === currentPatientId) {
      this.addResourceToPatientResources(resource, savedStatus);
    }
  }

  private addResourceToCurrentEncounterResources(resource: Resource, savedStatus: 'saved' | 'unsaved' = 'unsaved') {
    const referenceId = this.toRefId(resource);
    switch (resource.resourceType) {
      case 'Observation': {

        this.upsertToSubject(this.currentEncounterResources.observations, {
          referenceId,
          savedStatus,
          actualResource: resource as Observation
        });
        // Also reflect into patient resources if the encounter's patient matches the current patient
        this.maybeAddToPatientResources(resource, savedStatus);
        break;
      }
      case 'Condition': {
        this.upsertToSubject(this.currentEncounterResources.diagnosis, {
          referenceId,
          savedStatus,
          actualResource: resource as Condition
        });
        // Also reflect into patient resources if the encounter's patient matches the current patient
        this.maybeAddToPatientResources(resource, savedStatus);
        break;
      }
      case 'MedicationRequest': {
        this.upsertToSubject(this.currentEncounterResources.medicationRequests, {
          referenceId,
          savedStatus,
          actualResource: resource as MedicationRequest
        });
        // Also reflect into patient resources if the encounter's patient matches the current patient
        this.maybeAddToPatientResources(resource, savedStatus);
        break;
      }
      default:
        // Non-encounter-list resources are ignored here (e.g., Encounter itself).
        break;
    }
  }
  // currentPatientIdFromResolver = new BehaviorSubject<string | null>(null);
  public isEncounterActive(): boolean {
    const enc = this.currentEncounter.value;
    return !!enc && ['in-progress', 'planned', 'triaged'].includes(enc.status);
  }

  public persistLocalResource(resource: Resource, savedStatus: 'saved' | 'unsaved') {
    if (this.isResourceForCurrentEncounter(resource)) {
      this.addResourceToCurrentEncounterResources(resource, savedStatus);
    }
    // else {
    if (this.currentPatientId$.getValue()) {
      this.addResourceToPatientResources(resource, savedStatus);
    }

    // }
  }

  public persistPatientResource(resource: Resource, savedStatus: 'saved' | 'unsaved' = 'unsaved') {
    this.addResourceToPatientResources(resource, savedStatus);
  }


  public persistOrgWideResource(resource: Resource, savedStatus: 'saved' | 'unsaved') {
    this.addResourceToOrgWideResources(resource, savedStatus);
  }

  public buildCodeableConcept(raw: any): any {
    if (!raw) return null;
    if (typeof raw === 'string') {
      if (raw.includes('$#$')) {
        const [code = '', display = '', system = ''] = raw.split('$#$');
        return {
          coding: [{ code, display, system }],
          text: display || code
        };
      }
      return { text: raw };
    }
    if (raw.coding) return raw;
    return { text: String(raw) };
  }

  private buildObservationValue(values: any): { valueQuantity?: any; valueString?: string } {
    const rt = values?.value?.result_type;
    if (rt === 'Number') {
      const num = Number(values?.value?.result_value);
      if (!isNaN(num)) {
        return {
          valueQuantity: {
            value: num,
            unit: values?.value?.result_unit || undefined,
            system: 'http://unitsofmeasure.org',
            code: values?.value?.result_unit || undefined
          }
        };
      }
    }
    if (rt === 'Text') {
      const txt = values?.value?.result_value_text || values?.value?.result_value;
      if (txt) return { valueString: String(txt) };
    }
    return {};
  }

  /**
   * Build and persist an Observation from form values.
   * Expects keys: status, category, name, value (group), attachment(optional).
   * Returns { success, resource?, error? }.
   */
  public postObservation(values: any): { success: boolean; resource?: Observation; error?: string } {
    if (!this.isEncounterActive()) {
      return { success: false, error: 'No active encounter.' };
    }
    const patient = this.PatientResources.currentPatient.value?.actualResource;
    if (!patient?.id) {
      return { success: false, error: 'No current patient selected.' };
    }
    const encounterId = this.getCurrentEncounterId();
    if (!encounterId) {
      return { success: false, error: 'Encounter ID missing.' };
    }

    const status = (values?.status || 'preliminary').toLowerCase();
    const categoryCC = this.buildCodeableConcept(values?.category);
    const codeCC = this.buildCodeableConcept(values?.name);
    const valuePart = this.buildObservationValue(values);
    const nowIso = new Date().toISOString();

    const obs: Observation = {
      resourceType: 'Observation',
      status,
      subject: { reference: `Patient/${patient.id}` },
      encounter: { reference: `Encounter/${encounterId}` },
      effectiveDateTime: nowIso,
      // ...(categoryCC ? { category: [categoryCC] } : {}),
      category: categoryCC || { text: 'Uncategorized' },
      // ...(codeCC ? { code: codeCC } : {}),
      ...valuePart,
      code: codeCC || { text: 'Unnamed Observation' }
    };

    // Simple attachment handling -> valueAttachment if single
    if (values?.attachment && Array.isArray(values.attachment) && values.attachment.length) {
      const first = values.attachment[0];
      if (first?.data) {
        (obs as any).valueAttachment = {
          contentType: first.type || first.contentType || 'application/octet-stream',
          data: first.data,
          title: first.name || first.title
        };
      }
    }

    // Persist
    this.addResourceToCurrentEncounterResources(obs, 'unsaved');
    return { success: true, resource: obs };
  }

  /**
   * Get current patient data in various formats
   * @param format - 'id' | 'reference-string' | 'reference-object' | 'resource'
   * @returns Patient data in requested format, or null if not found
 

  /**
   * Helper: Extract display name from Patient resource
   */
  private getPatientDisplayName(patient: any): string | undefined {
    if (!patient?.name || !Array.isArray(patient.name) || patient.name.length === 0) {
      return undefined;
    }

    const name = patient.name[0];
    const given = Array.isArray(name.given) ? name.given.join(' ') : '';
    const family = name.family || '';

    const fullName = `${given} ${family}`.trim();
    return fullName || undefined;
  }

  /**
   * Clear all patient-scoped data when navigating away from patient routes
   * Should be called in ngOnDestroy of patient-scoped components
   * 
   * Implications of iterating and clearing:
   * ✅ Ensures complete cleanup - no stale data
   * ✅ Prevents memory leaks from accumulated arrays
   * ✅ Resets UI state consistently
   * ⚠️ Any active subscriptions will receive empty arrays
   * ⚠️ Components must handle empty states gracefully
   */
  clearCurrentPatientContext(): void {
    console.log('Clearing patient context from StateService');

    // Clear encounter
    this.setCurrentEncounter(null);

    // Clear patient ID from resolver
    this.currentPatientIdFromResolver.next(null);

    // Iterate through ALL PatientResources properties and reset them
    const resourceKeys = Object.keys(this.PatientResources) as Array<keyof typeof this.PatientResources>;

    resourceKeys.forEach(key => {
      const subject = this.PatientResources[key];

      if (key === 'currentPatient') {
        // Special handling for currentPatient (single object, not array)
        (subject as BehaviorSubject<{
          referenceId: null,
          savedStatus: 'saved' | 'unsaved',
          actualResource: Patient
        }>).next({
          referenceId: null,
          savedStatus: 'unsaved',
          actualResource: {} as Patient
        })
      } else {
        // All other resources are arrays
        (subject as BehaviorSubject<any[]>).next([]);
      }
    });

    console.log('Patient context cleared successfully - all PatientResources reset');
  }

  /**
   * Resets all org-wide cached FHIR resources to prevent stale cross-facility contamination
   * when switching hospitals or clearing session context.
   */
  clearOrgWideResources(): void {
    console.log('Clearing org-wide resources from StateService');
    const resourceKeys = Object.keys(this.orgWideResources) as Array<keyof typeof this.orgWideResources>;
    resourceKeys.forEach(key => {
      const subject = this.orgWideResources[key];
      if (subject && typeof (subject as any).next === 'function') {
        if (key === 'currentPatient') {
          (subject as BehaviorSubject<any>).next({
            referenceId: null,
            savedStatus: 'unsaved',
            actualResource: {} as Patient
          });
        } else {
          (subject as BehaviorSubject<any[]>).next([]);
        }
      }
    });
    console.log('Org-wide resources cleared successfully - all orgWideResources reset');
  }

  /**
   * ✅ NEW: Check if a patient has an active admission
   * An active admission is defined as:
   * - Encounter exists for the patient
   * - Encounter status: 'in-progress'
   * - Encounter class: 'IMP' (inpatient)
   * - Has hospitalization data (indicates admission)
   * 
   * @param patientId - Patient ID to check (defaults to current patient)
   * @returns Observable<boolean> that emits true if patient has active admission
   */
  isPatientAdmitted(patientId?: string): Observable<boolean> {
    return combineLatest([
      this.PatientResources.encounters,
      this.currentPatientId$
    ]).pipe(
      map(([encounters, currentPatId]) => {
        // Use provided patientId or fall back to current patient
        const targetPatientId = patientId || currentPatId;

        if (!targetPatientId) {
          console.warn('No patient ID provided and no current patient found');
          return false;
        }

        // Filter encounters for this specific patient
        const patientEncounters = encounters.filter(wrapper => {
          const encounter = wrapper.actualResource;
          const subjectRef = encounter?.subject?.reference;

          // Match "Patient/{id}" or just "{id}"
          return subjectRef === `Patient/${targetPatientId}` ||
            subjectRef === targetPatientId;
        });

        // Check if any encounter is an active admission
        const hasActiveAdmission = patientEncounters.some(wrapper => {
          const encounter = wrapper.actualResource;

          // Check if encounter is in-progress
          const isInProgress = encounter.status === 'in-progress';
          const isUndischargedFinished = encounter.status === 'finished' && !encounter.hospitalization?.dischargeDisposition;
          const isAdmittedStatus = isInProgress || isUndischargedFinished;

          // Check if encounter class is inpatient (IMP)
          const isInpatient = encounter.class?.code?.toUpperCase() === 'IMP' ||
            encounter.class?.display?.toLowerCase().includes('inpatient');

          // Check if has hospitalization data (indicates admission)
          const hasHospitalization = !!encounter.hospitalization;

          // Additional check: has admission identifier
          const hasAdmissionId = encounter.hospitalization?.preAdmissionIdentifier?.value ||
            encounter.identifier?.some((id: any) =>
              id.system?.includes('admission-id')
            );

          const isActive = isAdmittedStatus && isInpatient && hasAdmissionId;

          if (isActive) {
            console.log('🏥 Active admission found for patient:', {
              patientId: targetPatientId,
              encounterId: encounter.id,
              status: encounter.status,
              class: encounter.class,
              admissionId: encounter.hospitalization?.preAdmissionIdentifier?.value,
              hasHospitalization
            });
          }

          return isActive;
        });

        return hasActiveAdmission;
      }),
      distinctUntilChanged(), // Only emit when admission status changes
      shareReplay(1) // Cache latest result
    );
  }

  /**
   * ✅ NEW: Synchronous check if patient has active admission (uses cached value)
   * @param patientId - Patient ID to check (defaults to current patient)
   * @returns boolean indicating if patient has active admission
   * @deprecated Consider using isPatientAdmitted() observable for reactive updates
   */
  isPatientAdmittedSync(patientId?: string): boolean {
    const targetPatientId = patientId

    if (!targetPatientId) {
      console.warn('No patient ID provided and no current patient found');
      return false;
    }

    const encounters = this.PatientResources.encounters.getValue();

    // Filter encounters for this specific patient
    const patientEncounters = encounters.filter(wrapper => {
      const encounter = wrapper.actualResource;
      const subjectRef = encounter?.subject?.reference;

      return subjectRef === `Patient/${targetPatientId}` ||
        subjectRef === targetPatientId;
    });

    // Check if any encounter is an active admission
    return patientEncounters.some(wrapper => {
      const encounter = wrapper.actualResource;

      const isInProgress = encounter.status === 'in-progress';
      const isUndischargedFinished = encounter.status === 'finished' && !encounter.hospitalization?.dischargeDisposition;
      const isAdmittedStatus = isInProgress || isUndischargedFinished;
      const isInpatient = encounter.class?.code?.toUpperCase() === 'IMP' ||
        encounter.class?.display?.toLowerCase().includes('inpatient');
      const hasAdmissionId = encounter.hospitalization?.preAdmissionIdentifier?.value ||
        encounter.identifier?.some((id: any) =>
          id.system?.includes('admission-id')
        );

      return isAdmittedStatus && isInpatient && hasAdmissionId;
    });
  }

  /**
   * ✅ NEW: Get active admission encounter for a patient
   * @param patientId - Patient ID (defaults to current patient)
   * @returns Observable<Encounter | null>
   */
  getActiveAdmission(patientId?: string): Observable<Encounter | null> {
    return combineLatest([
      this.PatientResources.encounters,
      this.currentPatientId$
    ]).pipe(
      map(([encounters, currentPatId]) => {
        const targetPatientId = patientId || currentPatId;

        if (!targetPatientId) return null;

        // Filter encounters for this patient
        const patientEncounters = encounters.filter(wrapper => {
          const encounter = wrapper.actualResource;
          const subjectRef = encounter?.subject?.reference;

          return subjectRef === `Patient/${targetPatientId}` ||
            subjectRef === targetPatientId;
        });

        // Find the active admission encounter
        const activeAdmission = patientEncounters.find(wrapper => {
          const encounter = wrapper.actualResource;

          const isInProgress = encounter.status === 'in-progress';
          const isUndischargedFinished = encounter.status === 'finished' && !encounter.hospitalization?.dischargeDisposition;
          const isAdmittedStatus = isInProgress || isUndischargedFinished;
          const isInpatient = encounter.class?.code?.toUpperCase() === 'IMP' ||
            encounter.class?.display?.toLowerCase().includes('inpatient');
          const hasAdmissionId = encounter.hospitalization?.preAdmissionIdentifier?.value ||
            encounter.identifier?.some((id: any) =>
              id.system?.includes('admission-id')
            );

          return isAdmittedStatus && isInpatient && hasAdmissionId;
        });

        return activeAdmission?.actualResource || null;
      }),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      shareReplay(1)
    );
  }

  /**
   * Fetch active Medications from server and return as Reference data format.
   * Returns Observable of **active medications only** (filters by `status = 'active'`).
   * Does NOT check stock levels - returns all active medications regardless of stock.
   * 
   * Format: {reference: 'Medication/id', display: 'Medication name', type: 'Medication'}
   * 
   * @returns Observable<ReferenceDataType[]> - Array of active medication references
   */
  public fetchActiveMedicationReferences(): Observable<ReferenceDataType[]> {
    return this.http.get<Bundle<Medication>>(`${this.backendUrl}/Medication?_count=1000`).pipe(
      map((bundle: Bundle<Medication>) => {
        const medications = (bundle.entry || [])
          .map(entry => entry.resource as Medication)
          .filter(Boolean);

        console.log(`📦 Fetched ${medications.length} total medications from server`);

        // Filter to only active medications (ignore stock)
        const activeMedicationReferences: ReferenceDataType[] = medications
          .filter(med => {
            // Check if medication is active (status field)
            const statusActive = !med.status || med.status === 'active';
            return statusActive;
          })
          .map(med => {
            const baseDisplay = med.code?.text ||
              med.code?.coding?.[0]?.display ||
              med.code?.coding?.[0]?.code ||
              `Medication/${med.id}`;
            const display = baseDisplay + (med.batch?.lotNumber ? ` [Batch: ${med.batch.lotNumber}]` : '');

            return {
              reference: `Medication/${med.id}`,
              display,
              type: 'Medication'
            } as ReferenceDataType;
          });

        console.log(`✅ Filtered to ${activeMedicationReferences.length} active medications`);
        return activeMedicationReferences;
      }),
      catchError((error) => {
        console.error('❌ Error fetching active medications:', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /**
   * Fetch active Medications that are unexpired AND have stock available.
   * Filters by:
   * 1. status = 'active'
   * 2. batch.expirationDate >= today (not expired)
   * 3. extension.totalRemaining > 0 (has stock)
   * 
   * Format: {reference: 'Medication/id', display: 'Medication name (Stock: X)', type: 'Medication'}
   * 
   * @returns Observable<ReferenceDataType[]> - Array of unexpired active medication references with stock
   */
  public fetchUnexpiredActiveMedicationReferences(): Observable<ReferenceDataType[]> {
    return this.http.get<Bundle<Medication>>(`${this.backendUrl}/Medication?_count=1000`).pipe(
      map((bundle: Bundle<Medication>) => {
        const medications = (bundle.entry || [])
          .map(entry => entry.resource as Medication)
          .filter(Boolean);

        console.log(`📦 Fetched ${medications.length} total medications from server`);

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

        // Filter to only active, unexpired medications with stock
        const unexpiredActiveMedicationReferences: ReferenceDataType[] = medications
          .filter(med => {
            // Check 1: Status must be active
            const statusActive = !med.status || med.status === 'active';
            if (!statusActive) return false;

            // Check 2: Not expired (batch.expirationDate >= today)
            let isNotExpired = true;
            if (med.batch?.expirationDate) {
              const expirationDate = new Date(med.batch.expirationDate);
              isNotExpired = expirationDate >= today;
            }
            if (!isNotExpired) return false;

            // Check 3: Has stock (extension.totalRemaining > 0)
            const stockExtension = med.extension?.find(ext =>
              ext.url?.endsWith('totalRemaining') || ext.url?.includes('stock')
            );
            const stockLevel = stockExtension?.valueQuantity?.value || 0;
            const hasStock = stockLevel > 0;

            return hasStock;
          })
          .map(med => {
            const baseDisplay = med.code?.text ||
              med.code?.coding?.[0]?.display ||
              med.code?.coding?.[0]?.code ||
              `Medication/${med.id}`;
            const display = baseDisplay + (med.batch?.lotNumber ? ` [Batch: ${med.batch.lotNumber}]` : '');

            // Get stock level for display
            const stockExtension = med.extension?.find(ext => ext.url?.endsWith('totalRemaining'));
            const stockLevel = stockExtension?.valueQuantity?.value || 0;

            return {
              reference: `Medication/${med.id}`,
              display: `${display} (Stock: ${stockLevel})`,
              type: 'Medication'
            } as ReferenceDataType;
          });

        console.log(`✅ Filtered to ${unexpiredActiveMedicationReferences.length} unexpired active medications with stock`);
        return unexpiredActiveMedicationReferences;
      }),
      catchError((error) => {
        console.error('❌ Error fetching unexpired active medications:', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /**
   * Fetch Service Request CodeSystem from server and return active concepts only.
   * Returns an Observable that emits an array of active concept objects.
   * Each concept includes: code, display, system, and category.
   * 
   * @param codeSystemUrl - Optional CodeSystem URL to fetch (defaults to service-request CodeSystem)
   * @returns Observable<Array<{code: string, display: string, system: string, category?: string}>>
   */
  public fetchActiveServiceRequestCodes(
    codeSystemUrl?: string
  ): Observable<Array<{ code: string; display: string; system: string; category?: string }>> {
    // Default to service-request CodeSystem URL pattern
    const url = codeSystemUrl || `${this.backendUrl}/CodeSystem?url:contains=service-request&_count=1000`;

    return this.http.get<Bundle<CodeSystem>>(url).pipe(
      map((bundle: Bundle<CodeSystem>) => {
        // Extract CodeSystem resources from bundle
        const codeSystems = (bundle.entry || [])
          .map(entry => entry.resource as CodeSystem)
          .filter(Boolean);

        if (codeSystems.length === 0) {
          console.warn('⚠️ No CodeSystem found for service requests');
          return [];
        }

        // Use the first CodeSystem (or merge multiple if needed)
        const codeSystem = codeSystems[0];
        const systemUrl = codeSystem.url || '';

        // Extract concepts from the CodeSystem
        const concepts = (codeSystem.concept || []).map(concept => {
          // Check if concept has an 'active' property
          const activeProperty = concept.property?.find(p => p.code === 'active');
          const isActive = activeProperty?.valueBoolean !== false; // Default to true if not specified

          // Extract category property if it exists
          const categoryProperty = concept.property?.find(p => p.code === 'category');
          const category = categoryProperty?.valueString;

          return {
            code: concept.code || '',
            display: concept.display || concept.code || '',
            system: systemUrl,
            category,
            isActive
          };
        });

        // Filter to only active concepts
        const activeConcepts = concepts
          .filter(c => c.isActive)
          .map(({ isActive, ...rest }) => rest); // Remove isActive flag from final output

        console.log(`✅ Fetched ${activeConcepts.length} active service request codes (out of ${concepts.length} total)`);
        return activeConcepts;
      }),
      catchError((error) => {
        console.error('❌ Error fetching service request codes:', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }
}

